/**
 * Utility functions for strategem-related checks
 */

/**
 * Check if a strategem deals damage to units or generals
 * @param {Object} strategem - Strategem order object with id, description, abilities, etc.
 * @returns {boolean} - True if strategem deals damage
 */
export function doesStrategemDealDamage(strategem) {
  if (!strategem) return false;
  
  const description = strategem.description || '';
  const abilities = strategem.abilities || [];
  
  // Check description for damage keywords
  const damagePatterns = [
    /damage general for \d+/i,
    /damage target for \d+/i,
    /damage all enemy units for \d+/i,
    /deal \d+ damage to/i,
    /deal \d+ damage/i,
    /damage.*unit/i,
    /damage.*general/i
  ];
  
  // Check if description matches any damage pattern
  if (damagePatterns.some(pattern => pattern.test(description))) {
    return true;
  }
  
  // Check abilities array for damage effects
  if (Array.isArray(abilities)) {
    for (const ability of abilities) {
      const effect = typeof ability === 'object' ? ability.effect : ability;
      if (typeof effect === 'string') {
        if (damagePatterns.some(pattern => pattern.test(effect))) {
          return true;
        }
      }
    }
  }
  
  // Check if strategem has a damage property
  if (strategem.damage && strategem.damage > 0) {
    return true;
  }
  
  return false;
}

/**
 * Check if a strategem deals damage by ID (requires gameState to look up strategem)
 * @param {number} strategemId - Strategem ID
 * @param {Object} gameState - Game state object
 * @returns {boolean} - True if strategem deals damage
 */
export function doesStrategemDealDamageById(strategemId, gameState) {
  if (!strategemId || !gameState) return false;
  
  // Try to find strategem in hand (for current player or opponent)
  const players = gameState.state || {};
  for (const playerId in players) {
    const player = players[playerId];
    if (player?.hand) {
      const strategem = player.hand.find(order => order.id === strategemId);
      if (strategem) {
        return doesStrategemDealDamage(strategem);
      }
    }
  }
  
  // Try to find strategem in completedOrders (recently played)
  for (const playerId in players) {
    const player = players[playerId];
    if (player?.completedOrders) {
      // Check by ID first (type might not always be set)
      const strategem = player.completedOrders.find(order => order.id === strategemId);
      if (strategem) {
        // Additional check: verify it's actually a strategem (by type or by checking if it's in strategem implementations)
        const isStrategem = strategem.type === 'Strategem' || 
                           (strategem.type && /strategem/i.test(strategem.type)) ||
                           (strategem.description && /strategem/i.test(strategem.description));
        if (isStrategem) {
          return doesStrategemDealDamage(strategem);
        }
      }
    }
  }
  
  // If we can't find the strategem, default to false (no damage animation)
  // This is safer than showing damage for non-damage strategems
  return false;
}

/**
 * Check if a strategem destroys units (even without dealing damage)
 * @param {Object} strategem - Strategem order object with id, description, abilities, etc.
 * @returns {boolean} - True if strategem destroys units
 */
export function doesStrategemDestroyUnits(strategem) {
  if (!strategem) return false;
  
  const description = (strategem.description || '').toLowerCase();
  const strategemId = strategem.id;
  
  // Known strategems that destroy units without dealing damage
  // Strategem 51: Culling Protocol - Destroys all units with 2 attack or less in target zone
  // Strategem 54: Repurposed Warhead - Destroys all units in target zone
  // Strategem 55: Vein Sever - Destroys a unit with 2 attack rating or less
  // Strategem 59: Fracture Minds - Destroys a unit if 4+ Asrah units are deployed (otherwise immobilizes)
  // Strategem 60: Destroy target unit
  // Strategem 117: Field Dominator - Destroys a unit if you have a unit with higher attack rating deployed
  // Strategem 181: Tactical Breach - Destroys a damaged enemy unit
  // NOTE: Strategem 111 (Kinetron Blast) is NOT included - it decommissions units (returns to hand), not destroys them
  const destroyStrategemIds = [51, 54, 55, 59, 60, 117, 181];
  if (destroyStrategemIds.includes(strategemId)) {
    return true;
  }
  
  // Check description for destroy keywords
  // NOTE: /decommission.*unit/i is NOT included - decommissioning returns units to hand, not destroys them
  const destroyPatterns = [
    /destroy.*unit/i,
    /kill.*unit/i,
    /remove.*unit/i
  ];
  
  if (destroyPatterns.some(pattern => pattern.test(description))) {
    return true;
  }
  
  return false;
}

/**
 * Check if a strategem destroys units by ID (requires gameState to look up strategem)
 * @param {number} strategemId - Strategem ID
 * @param {Object} gameState - Game state object
 * @returns {boolean} - True if strategem destroys units
 */
export function doesStrategemDestroyUnitsById(strategemId, gameState) {
  if (!strategemId || !gameState) return false;
  
  // Try to find strategem in hand (for current player or opponent)
  const players = gameState.state || {};
  for (const playerId in players) {
    const player = players[playerId];
    if (player?.hand) {
      const strategem = player.hand.find(order => order.id === strategemId);
      if (strategem) {
        return doesStrategemDestroyUnits(strategem);
      }
    }
  }
  
  // Try to find strategem in completedOrders (recently played)
  for (const playerId in players) {
    const player = players[playerId];
    if (player?.completedOrders) {
      const strategem = player.completedOrders.find(order => order.id === strategemId);
      if (strategem) {
        const isStrategem = strategem.type === 'Strategem' || 
                           (strategem.type && /strategem/i.test(strategem.type)) ||
                           (strategem.description && /strategem/i.test(strategem.description));
        if (isStrategem) {
          return doesStrategemDestroyUnits(strategem);
        }
      }
    }
  }
  
  return false;
}

/**
 * Check if a strategem shrinks units (reduces health, attack rating, or both)
 * @param {Object} strategem - Strategem order object with id, description, abilities, etc.
 * @returns {boolean} - True if strategem shrinks units
 */
export function doesStrategemShrinkUnits(strategem) {
  if (!strategem) return false;
  
  const description = (strategem.description || '').toLowerCase();
  const strategemId = strategem.id;
  
  // Known shrink strategems by ID
  // Strategem 52: Shrink a unit by -4/-0 this round
  // Strategem 180: Touch of darkness - Shrink a unit by -1/-1
  // Strategem 183: Growing Darkness - Shrink a unit by -2/-2
  const shrinkStrategemIds = [52, 180, 183];
  if (shrinkStrategemIds.includes(strategemId)) {
    return true;
  }
  
  // Check description for shrink keywords
  const shrinkPatterns = [
    /shrink.*unit/i,
    /reduce.*unit/i,
    /-\d+\/-\d+/i,  // -X/-Y format (e.g., -1/-1, -2/-2)
    /-\d+\/-\d+.*round/i,  // -X/-Y this round
    /-\d+\/0/i,     // -X/0 format
    /0\/-\d+/i      // 0/-Y format
  ];
  
  if (shrinkPatterns.some(pattern => pattern.test(description))) {
    return true;
  }
  
  // Check abilities array for shrink effects
  const abilities = strategem.abilities || [];
  if (Array.isArray(abilities)) {
    for (const ability of abilities) {
      const effect = typeof ability === 'object' ? ability.effect : ability;
      if (typeof effect === 'string') {
        if (shrinkPatterns.some(pattern => pattern.test(effect))) {
          return true;
        }
      }
      // Check for shrink ability type
      if (typeof ability === 'object' && ability.type === 'shrink') {
        return true;
      }
    }
  }
  
  return false;
}

/**
 * Check if a strategem shrinks units by ID (requires gameState to look up strategem)
 * @param {number} strategemId - Strategem ID
 * @param {Object} gameState - Game state object
 * @returns {boolean} - True if strategem shrinks units
 */
export function doesStrategemShrinkUnitsById(strategemId, gameState) {
  if (!strategemId || !gameState) return false;
  
  // Try to find strategem in hand (for current player or opponent)
  const players = gameState.state || {};
  for (const playerId in players) {
    const player = players[playerId];
    if (player?.hand) {
      const strategem = player.hand.find(order => order.id === strategemId);
      if (strategem) {
        return doesStrategemShrinkUnits(strategem);
      }
    }
  }
  
  // Try to find strategem in completedOrders (recently played)
  for (const playerId in players) {
    const player = players[playerId];
    if (player?.completedOrders) {
      const strategem = player.completedOrders.find(order => order.id === strategemId);
      if (strategem) {
        const isStrategem = strategem.type === 'Strategem' || 
                           (strategem.type && /strategem/i.test(strategem.type)) ||
                           (strategem.description && /strategem/i.test(strategem.description));
        if (isStrategem) {
          return doesStrategemShrinkUnits(strategem);
        }
      }
    }
  }
  
  return false;
}

/**
 * Check if a strategem has a positive effect (heal, buff, etc.)
 * @param {Object} strategem - Strategem order object with id, description, abilities, etc.
 * @returns {boolean} - True if strategem has positive effect
 */
export function hasPositiveEffect(strategem) {
  if (!strategem) return false;
  
  const description = strategem.description || '';
  const abilities = strategem.abilities || [];
  const strategemId = strategem.id;
  
  // Known positive-effect strategems by ID
  // Strategem 62: Hive Surge - Grow friendly units in this zone by +2/+0 for one round
  // Strategem 182: Gift of the Rift - Grow a unit by +1/+1
  // Strategem 187: Quantum Surge - Choose a unit to attack again this turn
  const positiveStrategemIds = [62, 182, 187];
  if (positiveStrategemIds.includes(strategemId)) {
    return true;
  }
  
  // Check description for positive effect keywords
  const positivePatterns = [
    /heal.*general for \d+/i,
    /heal.*target for \d+/i,
    /heal.*unit/i,
    /restore.*unit/i,
    /grow.*unit/i,
    /give.*target.*\+(\d+).*attack.*\+(\d+).*health/i,
    /give.*target.*\+(\d+).*health.*\+(\d+).*attack/i,
    /buff.*unit/i,
    /\+(\d+)\/\+\d+/i, // +X/+Y format
    /\+(\d+)\/\d+/i,   // +X/Y format
  ];
  
  // Check if description matches any positive pattern
  if (positivePatterns.some(pattern => pattern.test(description))) {
    return true;
  }
  
  // Check abilities array for positive effects
  if (Array.isArray(abilities)) {
    for (const ability of abilities) {
      const effect = typeof ability === 'object' ? ability.effect : ability;
      if (typeof effect === 'string') {
        if (positivePatterns.some(pattern => pattern.test(effect))) {
          return true;
        }
      }
      // Check for heal/buff ability names
      if (typeof ability === 'object' && (ability.name === 'Heal' || ability.name === 'Buff')) {
        return true;
      }
    }
  }
  
  // Check if abilities array contains heal/buff strings
  if (abilities.includes('Heal') || abilities.includes('Buff')) {
    return true;
  }
  
  return false;
}

/**
 * Check if a strategem has a positive effect by ID (requires gameState to look up strategem)
 * @param {number} strategemId - Strategem ID
 * @param {Object} gameState - Game state object
 * @returns {boolean} - True if strategem has positive effect
 */
export function hasPositiveEffectById(strategemId, gameState) {
  if (!strategemId) return false;
  
  // First check known positive strategem IDs (fastest and most reliable)
  const positiveStrategemIds = [62, 182, 187];
  if (positiveStrategemIds.includes(strategemId)) {
    return true;
  }
  
  // If not in known list, try to find strategem in gameState to check description/abilities
  if (!gameState) return false;
  
  const players = gameState.state || {};
  for (const playerId in players) {
    const player = players[playerId];
    if (player?.hand) {
      const strategem = player.hand.find(order => order.id === strategemId);
      if (strategem) {
        return hasPositiveEffect(strategem);
      }
    }
  }
  
  // Try to find strategem in completedOrders (recently played)
  for (const playerId in players) {
    const player = players[playerId];
    if (player?.completedOrders) {
      const strategem = player.completedOrders.find(order => order.id === strategemId);
      if (strategem) {
        const isStrategem = strategem.type === 'Strategem' || 
                           (strategem.type && /strategem/i.test(strategem.type)) ||
                           (strategem.description && /strategem/i.test(strategem.description));
        if (isStrategem) {
          return hasPositiveEffect(strategem);
        }
      }
    }
  }
  
  return false;
}
